'use client';

import type { EntitlementFailure } from '@/lib/entitlement-failure';


import type { ExploreHandoff } from './chat';
import type { InputSlot, TemplateVariant } from './image-template';

/**
 * 视觉编辑器 - 类型定义
 */

// ==========================================
// 工具类型
// ==========================================

export type ToolType = 
  | 'select'      // 选择/移动
  | 'pencil'      // 画笔
  | 'eraser'      // 橡皮擦
  | 'rect'        // 矩形
  | 'circle'      // 圆形
  | 'line'        // 线条
  | 'text'        // 文字
  | 'image'       // 图片
  | 'pan';        // 平移画布

// ==========================================
// 图层类型
// ==========================================

export type LayerType = 'background' | 'foreground' | 'decoration' | 'text' | 'shape';

export interface Layer {
  id: string;
  type: LayerType;
  name: string;
  visible: boolean;
  locked: boolean;
  opacity: number;
  objects: CanvasObject[];
}

// ==========================================
// 画布对象
// ==========================================

export interface CanvasObjectBase {
  id: string;
  type: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  opacity: number;
  locked: boolean;
}

export interface ImageObject extends CanvasObjectBase {
  type: 'image';
  src: string;
}

export interface RectObject extends CanvasObjectBase {
  type: 'rect';
  fill: string;
  stroke: string;
  strokeWidth: number;
  cornerRadius?: number;
}

export interface CircleObject extends CanvasObjectBase {
  type: 'circle';
  radius: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
}

export interface TextObject extends CanvasObjectBase {
  type: 'text';
  text: string;
  fontSize: number;
  fontFamily: string;
  fill: string;
  fontWeight?: string;
  fontStyle?: string;
}

export interface PathObject extends CanvasObjectBase {
  type: 'path';
  path: string;  // SVG path data
  stroke: string;
  strokeWidth: number;
  fill?: string;
}

export type CanvasObject = ImageObject | RectObject | CircleObject | TextObject | PathObject;

// ==========================================
// 画板 (Artboard) - 每个分镜的画布区域
// ==========================================

export interface Artboard {
  x: number;              // 画板在无限画布中的 X 位置
  y: number;              // 画板在无限画布中的 Y 位置
  width: number;          // 画板宽度 (默认 1920)
  height: number;         // 画板高度 (默认 1080)
}

export const DEFAULT_ARTBOARD: Artboard = {
  x: 0,
  y: 0,
  width: 1920,
  height: 1080,
};

// ==========================================
// 分镜
// ==========================================

export type BackgroundType = 'original' | 'color' | 'image' | 'prompt' | 'template';

export interface ShotBackground {
  type: BackgroundType;
  color?: string;
  imageUrl?: string;
  prompt?: string;
  generatedImageUrl?: string;
  templateId?: string;
  canvasDataUrl?: string;
}

/** 画布节点内容类型（描述「是什么」，不描述「在哪」） */
export type CanvasNodeMediaType = 'video' | 'image' | '3d_model' | 'prompt' | 'audio' | 'sequence';

export type SmartEditHistoryMode = 'mask' | 'batch';
export type SmartEditAiActionKey = 'pose_change' | 'model_change' | 'generated_variant';
export type SmartEditOperationKind = 'local_edit' | 'remove_background' | 'image_edit';

/** Frozen at submission; never replace this revision with the current canvas revision. */
export interface SmartEditGenerationSource {
  inputStickerOverlays?: SmartEditStickerOverlay[];
  consumedStickerIds?: string[];
  inputAssetId?: string;
  inputImageUrl?: string;
  inputMediaBindingRevision?: number;
  sourceRevision?: string;
  requestId?: string;
  canvasNodeId?: string;
}

export interface SmartEditHistoryEntry extends SmartEditGenerationSource {
  id: string;
  imageUrl: string;
  thumbnailUrl?: string;
  isOriginal?: boolean;
  taskId?: string;
  outputAssetId?: string;
  prompt?: string;
  aspectRatio?: string;
  whRatio?: number;
  mode: SmartEditHistoryMode;
  actionKey?: SmartEditAiActionKey;
  operationKind?: SmartEditOperationKind;
  status?: 'pending' | 'completed' | 'failed' | 'cancelled';
  errorCode?: string;
  errorMessage?: string;
  createdAt: string;
  appliedAt?: string;
}

export type SmartEditTextFontPreset = 'clean' | 'editorial' | 'rounded' | 'poster' | 'mono';
export type SmartEditTextBackgroundPreset = 'none' | 'soft' | 'solid';
export type SmartEditTextShadowPreset = 'none' | 'soft' | 'dramatic';
export type SmartEditTextBubblePreset = 'none' | 'rounded' | 'speech';
export type SmartEditTextAnimationPreset = 'none' | 'fade-in' | 'pop' | 'rise' | 'typewriter' | 'pulse';
export type SmartEditTextFontId =
  | 'noto-sans-sc'
  | 'noto-serif-sc'
  | 'ma-shan-zheng'
  | 'zcool-xiaowei'
  | 'zcool-qingke-huangyou'
  | 'zcool-kuaile'
  | 'liu-jian-mao-cao'
  | 'long-cang'
  | 'zhi-mang-xing'
  | 'montserrat'
  | 'poppins'
  | 'bebas-neue';
export type SmartEditTextStylePresetId =
  | 'clean-title'
  | 'caption'
  | 'black-label'
  | 'editorial'
  | 'poster'
  | 'neon'
  | 'handwritten';

/** Center and dimensions are relative to the displayed image, independent of viewport zoom. */
export interface SmartEditStickerOverlay {
  id: string;
  assetId: string;
  sourceUrl: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  flipH: boolean;
  flipV: boolean;
}

export interface SmartEditTextOverlay {
  id: string;
  text: string;
  x: number;
  y: number;
  fontSizeRatio: number;
  lineHeightRatio: number;
  letterSpacingEm: number;
  color: string;
  colorOpacity: number;
  fontWeight: 400 | 500 | 600 | 700;
  fontStyle: 'normal' | 'italic';
  textDecoration: 'none' | 'underline';
  textAlign: 'left' | 'center' | 'right';
  fontId: SmartEditTextFontId;
  stylePresetId?: SmartEditTextStylePresetId;
  /**
   * Legacy preset fields are kept optional so old persisted nodes can be
   * normalized without losing compatibility.
   */
  fontPreset: SmartEditTextFontPreset;
  outlineWidthRatio: number;
  outlineColor: string;
  outlineOpacity: number;
  backgroundPreset: SmartEditTextBackgroundPreset;
  backgroundColor: string;
  backgroundOpacity: number;
  backgroundRadiusRatio: number;
  backgroundPaddingXRatio: number;
  backgroundPaddingYRatio: number;
  bubblePreset: SmartEditTextBubblePreset;
  shadowPreset: SmartEditTextShadowPreset;
  shadowColor: string;
  shadowOpacity: number;
  shadowBlurRatio: number;
  shadowOffsetXRatio: number;
  shadowOffsetYRatio: number;
  glowColor: string;
  glowOpacity: number;
  glowBlurRatio: number;
  curveRatio: number;
  animationPreset: SmartEditTextAnimationPreset;
  rotation: number;
  scale: number;
}

// ── Sequence Area 串联区域 ──

/** 串联区域内的单个片段引用 */
export interface SequenceClipOutput {
  id: string;
  videoUrl: string;
  thumbnail?: string;
  taskId?: string;
  createdAt?: number;
}

export interface SequenceClip {
  id: string;               // clip-{uuid}
  sourceNodeId: string;      // 引用的画布 node ID
  order: number;             // 排序序号
  duration: number;          // 毫秒（图片默认 3000ms）
  trimStart?: number;        // 裁剪起点（毫秒），默认 0
  trimEnd?: number;          // 裁剪终点（毫秒），默认 = duration
  playbackRate?: number;     // 播放倍率（1 = 原速），用于变速后真实改变时间线时长
  sourceDuration?: number;   // 源视频实际最大时长（毫秒），用于限制拖拽上限
  thumbnail?: string;
  videoUrl?: string;
  mediaType: 'video' | 'image';
  prompt?: string;            // 用户可编辑的 clip 描述/prompt
  label?: string;
  directorBeatId?: string;
  status?: 'planned' | 'ready' | 'generated';
  selectedOutputId?: string;
  outputs?: SequenceClipOutput[];
}

export interface SequencePackagingState {
  subtitlePreset: 'hook_subtitles' | 'minimal_subtitles' | 'none';
  titleCardText?: string;
  ctaText?: string;
  safeZonePreset: 'vertical_focus' | 'center_safe' | 'wide_master';
  hints?: string[];
  outputVariantIds?: string[];
}

export interface SequenceDirectorMetadata {
  contentProfile?: 'portrait' | 'product' | 'scene' | 'editorial';
  targetAspectRatio?: string;
  targetDurationMs?: number;
  mustKeepSignals?: string[];
  primaryFacts?: string[];
}

export interface SequenceAudioTrack {
  id: string;
  name?: string;
  sourceUrl: string;
  assetId?: string;
  start: number;      // 毫秒
  duration: number;   // 毫秒
  volume: number;
  isMuted?: boolean;
}

export interface SequenceTextOverlay {
  id: string;
  text: string;
  start: number;      // 毫秒
  duration: number;   // 毫秒
  position: 'top' | 'center' | 'bottom';
  style?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    fontWeight?: 400 | 500 | 600 | 700;
  };
}

export interface SequenceSticker {
  id: string;
  name?: string;
  sourceUrl: string;
  assetId?: string;
  start: number;      // 毫秒
  duration: number;   // 毫秒
  position: { x: number; y: number }; // 0-1 归一化位置
  scale: number;
  rotation: number;
}

export interface SequenceExportSettings {
  resolution: '720P' | '1080P';
  aspectRatio?: '9:16' | '1:1' | '16:9';
}

export interface BrandStoryboardShot {
  shot_id: string;
  index: number;
  duration_sec: number;
  panel_image_url?: string;
  /** Direct image task currently replacing this panel. Persisted for refresh recovery. */
  panel_task_id?: string;
  panel_node_id?: string;
  spec_node_id?: string;
  /** The one responsibility this beat contributes to the whole film. */
  beat_role?: string;
  visual?: string;
  action?: string;
  camera?: string;
  product_exposure?: string;
  action_in?: string;
  action_out?: string;
  cut_reason?: string;
  transition?: string;
  /** This frame's local instruction; shared visual rules live on the board. */
  image_prompt?: string;
  /** This beat's local motion/cut instruction; it is compiled once for the final video. */
  video_prompt?: string;
  user_direction?: string;
  status?: string;
}

/** Model-facing reference state; several anchors may support one actual timeline shot. */
export interface BrandStoryboardReferenceAnchor {
  anchor_id: string;
  shot_id: string;
  order: number;
  role: 'opening' | 'cut' | 'closing' | string;
  image_prompt?: string;
  panel_image_url?: string;
  panel_task_id?: string;
  panel_node_id?: string;
  status?: string;
}

export interface BrandStoryboardVisualPlan {
  version: number;
  revision: number;
  visual_mechanism: {
    id: string;
    label: string;
    label_zh?: string;
    label_en?: string;
    cast_policy: string;
    motion_level: string;
    edit_grammar: string;
  };
  variation_budget: {
    axes: string[];
    max_actual_shots: number;
    no_narrative: boolean;
    no_person_interaction: boolean;
    no_ugc_performance: boolean;
  };
  timing: { duration_seconds: number; video_config_revision: number };
}

export interface BrandStoryboardData {
  storyboard_id: string;
  revision: number;
  board_node_id?: string;
  board_image_url?: string;
  board_task_ids?: string[];
  layout?: { rows: number; cols: number };
  shots: BrandStoryboardShot[];
  /** Actual generation references, independent from timeline shot count. */
  reference_anchors?: BrandStoryboardReferenceAnchor[];
  /** Required production grammar for this board revision. */
  visual_plan?: BrandStoryboardVisualPlan;
  /** Bump when the stored prompt fields follow the compact global-plus-local contract. */
  prompt_contract_version?: number;
  /** Shared visual rule applied once when an individual storyboard panel is regenerated. */
  image_generation_contract?: string;
  /** Shared continuous-film rule; local beat instructions are compiled only at final submission. */
  final_video_prompt?: string;
  /** Explicit user confirmation when the selected video settings need review. */
  video_generation?: {
    confirmed?: boolean;
    provider?: 'kling' | 'gv' | 'seedance' | 'hailuo' | 'heygen';
    model?: string;
    duration_seconds?: number;
    aspect_ratio?: string;
      resolution?: string;
      revision?: number;
  };
  brand_video_task_id?: string;
  /** Revision that created the latest brand video task; only a matching revision can represent this board's output. */
  brand_video_task_revision?: number;
  status?: string;
}

/** 串联区域数据 */
export interface SequenceData {
  kind?: 'brand_storyboard_board' | 'ugc_storyboard' | 'ugc_final_assembly';
  clips: SequenceClip[];     // 有序片段列表
  title: string;             // 可编辑标题
  brandStoryboard?: BrandStoryboardData;
  packaging?: SequencePackagingState;
  directorMetadata?: SequenceDirectorMetadata;
  audioTracks?: SequenceAudioTrack[];
  textOverlays?: SequenceTextOverlay[];
  stickers?: SequenceSticker[];
  exportSettings?: SequenceExportSettings;
}

export interface WorkflowPresetBindingRef {
  presetId: string;
  bindingId: string;
  sourceStageId: string;
  sourceTemplateNodeId: string;
  sourceRunId?: string | null;
  sourceOutputNodeId?: string | null;
  outputIndex?: number;
  relationType?: NodeRelationType;
  relationLabel?: string;
}

/** 单条能力推荐（带 confidence 排序） */
export interface AdviseTag {
  id: string;           // 'face_swap', 'i2v_source', etc.
  confidence: number;   // 0.0-1.0
}

export interface CanvasTemplateSlotValue {
  sourceUrl?: string;
  previewUrl?: string;
  sourceNodeId?: string;
  assetId?: string | null;
  text?: string;
  selectValue?: string;
  workflowBinding?: WorkflowPresetBindingRef | null;
}

export interface CanvasTemplateRun {
  id: string;
  taskId?: string;
  taskIds?: string[];
  outputNodeId?: string;
  outputNodeIds?: string[];
  status: 'idle' | 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  syncState?: 'live' | 'reconnecting';
  lastPolledAt?: string;
  progress?: number;
  totalCount?: number;
  completedCount?: number;
  failedCount?: number;
  error?: string | null;
  createdAt: string;
}

export interface CanvasTemplateInstance {
  templateId: string;
  templateUpdatedAt?: string;
  name: string;
  nameZh?: string;
  coverUrl?: string | null;
  outputType: 'image' | 'video';
  capability: string;
  basePromptTemplate?: string;
  promptTemplate?: string;
  localizedPromptTemplates?: Partial<Record<'en' | 'zh', string>>;
  promptSourceNodeId?: string | null;
  modelParams: Record<string, unknown>;
  inputSlots: InputSlot[];
  variants?: TemplateVariant[];
  selectedVariantKey?: string | null;
  values: Record<string, CanvasTemplateSlotValue>;
  params: Record<string, unknown>;
  outputCount: number;
  workflowCheckpointEnabled?: boolean;
  activeRun?: CanvasTemplateRun | null;
  runHistory?: CanvasTemplateRun[];
  createdAt: string;
  updatedAt: string;
}

/** 风格分析结果（由后台 image_tagger 自动生成） */
export interface StyleAnalysis {
  status: 'pending' | 'analyzing' | 'completed' | 'failed';
  analyzedAt?: string;
  styles?: string[];       // ['minimal', 'editorial']
  moods?: string[];        // ['elegant', 'refined']
  colors?: string[];       // ['cool_tone', 'monochrome']
  scene?: string;          // 'studio'
  lighting?: string;       // 'studio_light'
  bodyFraming?: string;    // 'half_body'
  qualityScore?: number;   // 0.85
  description?: string;    // AI 生成的图片描述
  contentType?: string;    // 'portrait', 'product', 'fashion_editorial', etc.
  faceVisibility?: string; // 'clear_frontal', 'clear_side', 'partial', 'occluded', 'none'
  isIsolatedObject?: boolean;
  useTags?: string[];      // ['face_ref', 'skin_enhance'] — 向后兼容
  adviseTags?: AdviseTag[]; // 带 confidence 排序的推荐列表
  error?: string;          // 失败时的错误信息
}

export interface CanvasGridPanelEditSlot {
  panelIndex: number;
  row: number;
  col: number;
  label: string;
  previewUrl: string | null;
  sourceUrl: string | null;
  originalPreviewUrl: string | null;
  originalSourceUrl: string | null;
  originalLabel: string | null;
}

export interface CanvasGridPanelEditData {
  sourceNodeId: string;
  rows: number;
  cols: number;
  boardImageUrl: string | null;
  initialSlots: CanvasGridPanelEditSlot[];
}

export interface CanvasDirectorBoardData {
  schemaVersion: 1;
  sceneId?: string | null;
  sourceCanvasNodeId?: string | null;
  renderJobId?: string | null;
  snapshotUrl?: string | null;
  outputCanvasNodeId?: string | null;
  sourceCanvasNodeIds?: string[];
  referenceAssetRoles?: Record<string, string>;
  status?: 'draft' | 'rendering' | 'completed' | 'failed';
}

/** 统一画布节点 — 合并原 Shot + FreeNode + CanvasPromptNode */
export interface CanvasNode {
  kagemaneTemplateId?: string;
  kagemaneTemplateVersion?: number;
  id: string;
  /** Server binding generation observed by this node snapshot. */
  mediaBindingRevision?: number;
  /** 内容类型（替代旧 node_type='sequence'/'free'，现在只描述内容） */
  mediaType: CanvasNodeMediaType;
  
  // ── 通用字段（原 Shot & FreeNode 共享） ──
  thumbnail?: string;
  originalImageUrl?: string;
  videoUrl?: string;
  /** 3D 模型 GLB URL */
  modelUrl?: string;
  assetId?: string;
  /** 公共图库素材的显式选择证据；候选态不得写入画布节点。 */
  referenceEvidence?: {
    contractVersion: 'reference-evidence-v1';
    sourceKind: 'public_gallery_search' | 'public_gallery_browse';
    selectionState: 'user_confirmed';
    candidateId: string;
  };
  duration: number;           // 秒
  /** 源视频原始时长（秒），用于裁剪滑块上限，不随 trim 改变 */
  sourceDuration?: number;
  /** 当前片段在时间线中的起点（秒） */
  startTime?: number;
  /** 当前片段在时间线中的终点（秒） */
  endTime?: number;
  /** 当前片段在源视频中的起点（毫秒） */
  sourceStart?: number;
  /** 当前片段在源视频中的终点（毫秒） */
  sourceEnd?: number;
  aspectRatio?: string;
  /** 精确宽高比 width/height，优先级高于 aspectRatio */
  whRatio?: number;
  position: { x: number; y: number };
  /** 首次进入画布可见的时间，用于整理画布按进入顺序稳定排序 */
  enteredCanvasAt?: string;
  /** 最近一次有意义创作操作触达该节点的时间，用于最近活动排序与聚焦 */
  lastActivityAt?: string;
  /** 后端创建时间，用于自动整理画布时稳定按时间排序 */
  createdAt?: string;
  /** 后端更新时间，仅用于需要恢复/调试排序的场景 */
  updatedAt?: string;
  
  // ── AI 生成状态（临时，任务完成后清除） ──
  generatingTaskId?: string;
  generatingCapability?: string;
  /** ★ AI 任务失败时的错误信息 */
  generatingError?: string;
  entitlementFailure?: EntitlementFailure;
  /** 区分上传/生成等失败来源，避免 UI 把上传失败误报成生成失败 */
  generatingErrorKind?: 'upload' | 'generation' | 'submission';

  // ── AI 生成溯源（永久，任务完成后回写） ──
  /** 生成此节点的任务 ID（永久记录，不同于临时的 generatingTaskId） */
  generatedByTaskId?: string;
  /** 后端已物化的 UGC 最终合成视频；用于在普通 Canvas 视频节点上展示成品操作。 */
  ugcFinalAssembly?: boolean;
  /** 生成此节点所用的 prompt */
  generationPrompt?: string;
  /** 生成此节点所用的能力（face_swap / image_to_video / ...） */
  generationCapability?: string;

  /** ★ 节点来源标签（持久化）：如 "元素拆解"、"Lookbook"、"AI 生成"、"用户上传" 等 */
  sourceLabel?: string;

  /** ★ 生成分组 ID — 同一次 AI 操作产出的所有节点共享此 ID，用于宫格布局分组 */
  generationGroupId?: string;
  /** 同一任务的多输出序号，例如 Midjourney 固定四候选的 0-3。 */
  outputIndex?: number;

  /** ★ 溯源来源节点 ID 列表（持久化），用于恢复/补建 lineage 边 */
  lineageSourceNodeIds?: string[];
  /** ★ 溯源边类型（持久化），用于恢复/补建 lineage 边 */
  lineageRelationType?: NodeRelationType;
  /** ★ 溯源边标签（持久化），用于恢复/补建 lineage 边 */
  lineageRelationLabel?: string;

  /** Canvas-native reusable template instance stored in canvas_nodes.metadata.template_instance */
  templateInstance?: CanvasTemplateInstance;
  /** Director Board scene metadata stored in canvas_nodes.metadata.director_board */
  directorBoard?: CanvasDirectorBoardData;

  // ── 风格分析结果（P0: 上传即分析） ──
  /** ★ 后台自动分析的风格属性，用于画布标签展示 + Chat 上下文注入 */
  styleAnalysis?: StyleAnalysis;
  /** 跳过后台自动风格分析，用于外部参考素材等不需要 VLM 预分析的节点 */
  skipStyleAnalysis?: boolean;
  
  // ── 媒体节点字段 ──
  replacedVideoUrl?: string;
  transcript?: string;
  thumbnailUrl?: string;
  /** Asset-owned small preview, independent of the original media binding. */
  assetThumbnailUrl?: string;
  foregroundMaskUrl?: string;
  background?: ShotBackground;
  layers?: Layer[];
  artboard?: Artboard;
  viewportTransform?: number[];
  
  // ── 上传状态 ──
  isUploading?: boolean;
  uploadProgress?: number;
  uploadStage?: 'preparing' | 'uploading' | 'retrying' | 'verifying';
  uploadEtaSeconds?: number;
  /** 原文件仍在当前页面内存中，可由用户手动重试 */
  canRetryUpload?: boolean;
  /** 重试动作的真实边界：上传失败重传字节，持久化失败只重试落库。 */
  uploadRetryMode?: 'upload' | 'persistence';
  
  // ── 自由节点特有 ──
  isEmpty?: boolean;
  _decompositionParentId?: string;
  /** 持久化版：元素拆解父节点 ID（存入 metadata.decomposition_parent_id） */
  decompositionParentId?: string;
  /** preparing 节点创建时间戳，用于超时检测 */
  _preparingCreatedAt?: number;
  /** chain 占位节点标记：Shot 2-4 等待 Shot 1 完成才能创建，超时阈值更长 */
  _isChainPending?: boolean;
  /** 元素拆解：该节点对应的元素索引（用于轮询恢复时匹配 task.metadata.elements[i]） */
  _elementIndex?: number;
  
  // ── Prompt 节点特有 ──
  promptVariant?: 'prompt' | 'negative';
  promptText?: string;
  promptMetadata?: Record<string, unknown>;
  storyboardData?: Record<string, unknown>;
  gridPanelEditData?: CanvasGridPanelEditData;
  
  // ── 图片变换 ──
  rotation?: number;       // 0, 90, 180, 270
  flipH?: boolean;         // 水平镜像
  flipV?: boolean;         // 垂直镜像

  // ── 调色 ──
  colorAdj?: {
    adjustmentMasks?: import('@/lib/utils/image-picture').PictureMask[];
    enabled?: Partial<Record<'basic'|'hsl'|'curves'|'wheels'|'lut'|'effects', boolean>>;
    picture?: import('@/lib/utils/image-picture').ImagePicture;
    retouch?: import('@/lib/utils/image-retouch').ImageRetouch;
    filter?: import('@/lib/utils/image-filters').ImageFilter;
    grade?: import('@/lib/utils/image-color-grade').ImageColorGrade;
    exposure?: number;     // -100 - 100, default 0
    brightness?: number;   // 0.5 - 1.5, default 1
    contrast?: number;     // 0.5 - 1.5, default 1
    highlights?: number;   // -100 - 100, default 0
    shadows?: number;      // -100 - 100, default 0
    whitePoint?: number;   // -100 - 100, default 0
    blackPoint?: number;   // -100 - 100, default 0
    saturate?: number;     // 0 - 2, default 1
    temperature?: number;  // -30 - 30 (hue-rotate deg), default 0
    hue?: number;          // -180 - 180 (hue-rotate deg), default 0
    sepia?: number;        // 0 - 1, default 0
    grayscale?: number;    // 0 - 1, default 0
    hsl?: {
      red?: { hue?: number; saturation?: number; lightness?: number };
      orange?: { hue?: number; saturation?: number; lightness?: number };
      yellow?: { hue?: number; saturation?: number; lightness?: number };
      green?: { hue?: number; saturation?: number; lightness?: number };
      aqua?: { hue?: number; saturation?: number; lightness?: number };
      blue?: { hue?: number; saturation?: number; lightness?: number };
      purple?: { hue?: number; saturation?: number; lightness?: number };
      magenta?: { hue?: number; saturation?: number; lightness?: number };
    };
  };

  // ── Smart Edit 节点级生成历史 ──
  smartEditHistory?: SmartEditHistoryEntry[];
  textOverlays?: SmartEditTextOverlay[];
  stickerOverlays?: SmartEditStickerOverlay[];

  // ── Sequence Area 特有 ──
  sequenceData?: SequenceData;

  // ── 节点分组（折叠/展开） ──
  /** 所属分组 ID — 被收纳进某个 group 时有值 */
  groupId?: string;
  /** 分组内子节点 ID 列表 — 仅 group 容器节点有值 */
  groupChildIds?: string[];
  /** 分组是否收纳（collapsed=true 时子节点隐藏） */
  groupCollapsed?: boolean;
  /** 分组名称 */
  groupLabel?: string;
  /** 持久化的 workflow area 归属；用于稳定恢复模板工作流区域与其关联节点 */
  workflowAreaGroupId?: string;
}

// ── 向后兼容别名 ──
/** @deprecated 使用 CanvasNodeMediaType */
export type ShotMediaType = CanvasNodeMediaType;
/** @deprecated 使用 CanvasNode */
export type Shot = CanvasNode;
/** @deprecated 使用 CanvasNode */
export type FreeNode = CanvasNode;
/** @deprecated 使用 CanvasNode — prompt 节点 variant→promptVariant, text→promptText */
export type CanvasPromptNode = CanvasNode;

/** 画布上用户手动创建的连线（自由节点之间，或自由节点与序列节点之间） */
export interface CanvasEdge {
  id: string;
  source: string;         // 源节点 ID
  target: string;         // 目标节点 ID
  sourceHandle?: string;  // 源 Handle ID（PromptNode 等特殊节点需要）
  targetHandle?: string;  // 目标 Handle ID（PromptNode 等特殊节点需要）
  /** ★ 关联关系类型（可选，无则为普通连线） */
  relationType?: NodeRelationType;
  /** ★ 关联关系说明标签 */
  relationLabel?: string;
  /** 后端创建时间，用于整理画布时稳定排序同层关系 */
  createdAt?: string;
}

// ==========================================
// 节点关联关系（溯源 / Lineage）
// ==========================================

/** 节点关联关系类型 */
export type NodeRelationType =
  | 'split'            // 视频拆分：A 拆出 B
  | 'ai-generated'     // AI 生成：A 通过 AI 生成 B
  | 'bg-replace'       // 背景替换：A 换背景生成 B
  | 'extract-frame'    // 提取帧：从视频 A 中提取关键帧 B
  | 'separation'       // 抠图分层：A 分离前景/背景得到 B（旧版）
  | 'decomposition'    // 元素拆解：A 拆出人脸/服装/配饰 B
  | 'reference'        // 参考引用：B 参考了 A 的内容
  | 'composite'        // 合成：A + B → C（多输入合成）
  | 'duplicate'        // 复制：A 复制为 B
  | 'transition'       // 转场生成：A 和 C 之间生成转场 B
  | 'style-transfer'   // 风格迁移：A 风格应用到 B
  | 'custom';          // 自定义关联

/** 关联关系的视觉配置 */
export interface RelationTypeConfig {
  type: NodeRelationType;
  label: string;
  color: string;
  icon: string;          // Lucide icon name
  dashArray?: string;    // SVG stroke-dasharray
  description: string;
}

/** 预定义的关联关系视觉配置 */
export const RELATION_TYPE_CONFIGS: Record<NodeRelationType, RelationTypeConfig> = {
  'split':           { type: 'split',          label: 'Split',           color: '#3b82f6', icon: 'Scissors',     description: 'Split from source video' },
  'ai-generated':    { type: 'ai-generated',   label: 'AI Generated',    color: '#8b5cf6', icon: 'ImageIcon',     description: 'Generated via AI' },
  'bg-replace':      { type: 'bg-replace',     label: 'BG Replace',      color: '#06b6d4', icon: 'ImagePlus',    description: 'Generated with replaced background' },
  'extract-frame':   { type: 'extract-frame',  label: 'Extract Frame',   color: '#f59e0b', icon: 'Frame',        description: 'Key frame extracted from video' },
  'separation':      { type: 'separation',     label: 'Separate',        color: '#10b981', icon: 'Layers',       description: 'Foreground/background separation' },
  'decomposition':   { type: 'decomposition',  label: 'Decompose',       color: '#14b8a6', icon: 'Puzzle',       description: 'Face/garment/accessory decomposition' },
  'reference':       { type: 'reference',      label: 'Reference',       color: '#6b7280', icon: 'Link',         dashArray: '6 3', description: 'Reference relationship' },
  'composite':       { type: 'composite',      label: 'Composite',       color: '#ec4899', icon: 'Combine',      description: 'Multi-asset composite generation' },
  'duplicate':       { type: 'duplicate',       label: 'Duplicate',      color: '#78716c', icon: 'Copy',         dashArray: '4 2', description: 'Duplicated from source node' },
  'transition':      { type: 'transition',     label: 'Transition',      color: '#a855f7', icon: 'ArrowRightLeft', description: 'Transition effect generation' },
  'style-transfer':  { type: 'style-transfer', label: 'Style Transfer',  color: '#f43f5e', icon: 'Palette',      description: 'Style transfer generation' },
  'custom':          { type: 'custom',         label: 'Custom',          color: '#64748b', icon: 'Link2',        dashArray: '8 4', description: 'Custom relationship' },
};

// ==========================================
// 编辑器状态
// ==========================================

export interface CanvasState {
  zoom: number;
  panX: number;
  panY: number;
  selectedObjectIds: string[];
}

export interface ToolState {
  activeTool: ToolType;
  brushColor: string;
  brushSize: number;
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  fontSize: number;
  fontFamily: string;
}

// ==========================================
// Timeline
// ==========================================

export type TimelinePanelState = 'collapsed' | 'half' | 'expanded';
export type SegmentMediaType = 'video' | 'image' | 'ai-generated' | 'transition';

export interface TimelineSegment {
  id: string;
  sourceNodeId: string;
  order: number;
  mediaType: SegmentMediaType;
  durationMs: number;
  thumbnail?: string;
  label?: string;
  mediaUrl?: string;
  transcript?: string;
}

export interface TimelineState {
  segments: TimelineSegment[];
  totalDurationMs: number;
  panelState: TimelinePanelState;
}

export interface PendingVideoEntry {
  id: string;
  status: 'queued' | 'materializing_source' | 'building_sequence' | 'opening_preview';
  targetProjectId: string;
  imageUrl: string;
  imageThumbnail?: string;
  imageDescription?: string;
  handoff: ExploreHandoff;
}

export interface WorkflowAreaBounds {
  groupId: string;
  nodeIds: string[];
  x: number;
  y: number;
  width: number;
  height: number;
}

export const DEFAULT_TIMELINE_STATE: TimelineState = {
  segments: [],
  totalDurationMs: 0,
  panelState: 'collapsed',
};

export interface VisualEditorState {
  // 项目信息
  projectId: string | null;
  projectName: string;
  projectRole: 'owner' | 'editor' | 'viewer' | null;
  
  // 加载状态
  isLoading: boolean;
  isAnalyzing: boolean;
  isSaving: boolean;
  error: string | null;

  // Explore -> Canvas 视频首稿入口
  pendingVideoEntry: PendingVideoEntry | null;
  
  // ★ 统一节点数组（替代 shots + freeNodes + promptNodes）
  nodes: CanvasNode[];
  currentShotId: string | null;
  
  // 画布状态
  canvas: CanvasState;
  
  // 工具状态
  tool: ToolState;
  
  // UI 状态
  leftPanelTab: 'layers';
  rightPanelTab: 'background' | 'properties';
  isPlaying: boolean;
  
  // ★ 侧边栏状态
  // chatSidebarOpen: 独立控制 Chat 对话面板的展开/收起，仅由用户操作改变
  // activeSidebar: 控制非 Chat 面板（aiCapability, materialPicker, smartEdit, taskHistory, templateInspector）
  chatSidebarOpen: boolean;
  activeSidebar: 'taskHistory' | 'aiCapability' | 'materialPicker' | 'chat' | 'smartEdit' | 'templateInspector' | null;
  selectedClipIdForAI: string | null;  // 当前选中要应用 AI 能力的 clip
  templateInspectorNodeId: string | null;  // 当前在 workflow inspector 中编辑的模板节点 ID
  smartEditNodeId: string | null;  // 当前打开智能编辑面板的节点 ID
  smartEditTool: 'brush' | 'eraser' | null;  // 画笔工具
  smartEditBrushSize: number;
  smartEditBrushColor: string;
  smartEditMaskDataUrl: string | null;  // mask 数据
  smartEditPrompt: string;
  smartEditGlobalInstruction: string;
  smartEditPendingCount: number;
  smartEditHasLocalChanges: boolean;
  smartEditLocalEditsDone: boolean;
  smartEditSubmitting: boolean;
  
  // ★ 节点拖拽到 Chat 的视觉反馈标记
  isDraggingToChat: boolean;

  // ★ Sequence 编辑态：打开序列编辑弹窗时，隐藏所有侧边栏和浮动 UI
  sequenceFocusMode: boolean;
  activeSequenceEditorId: string | null;

  // ★ 画布交互模式（select = 框选, pan = 拖动画布）
  canvasInteractionMode: 'select' | 'pan';
  
  // ★ 画布连线
  canvasEdges: CanvasEdge[];

  // ★ 锁定节点（不可拖拽）
  lockedNodeIds: string[];

  // ★ Workflow area 运行时空间快照，供自动放置/互斥逻辑共享
  workflowAreaBounds: WorkflowAreaBounds[];

  // ★ 主线 Timeline
  timeline: TimelineState;
}

// ==========================================
// 默认值
// ==========================================

export const DEFAULT_TOOL_STATE: ToolState = {
  activeTool: 'select',
  brushColor: '#000000',
  brushSize: 4,
  fillColor: '#ffffff',
  strokeColor: '#000000',
  strokeWidth: 2,
  fontSize: 24,
  fontFamily: 'Inter',
};

export const DEFAULT_CANVAS_STATE: CanvasState = {
  zoom: 1,
  panX: 0,
  panY: 0,
  selectedObjectIds: [],
};
export const DEFAULT_SMART_EDIT_BRUSH_SIZE = 4;
export const DEFAULT_SMART_EDIT_BRUSH_COLOR = '#ef4444';

export const DEFAULT_VISUAL_EDITOR_STATE: VisualEditorState = {
  projectId: null,
  projectName: 'Untitled Project',
  projectRole: null,
  isLoading: true,
  isAnalyzing: false,
  isSaving: false,
  error: null,
  pendingVideoEntry: null,
  nodes: [],
  currentShotId: null,
  canvas: DEFAULT_CANVAS_STATE,
  tool: DEFAULT_TOOL_STATE,
  leftPanelTab: 'layers',
  rightPanelTab: 'background',
  isPlaying: false,
  chatSidebarOpen: true,
  activeSidebar: null,
  selectedClipIdForAI: null,
  templateInspectorNodeId: null,
  smartEditNodeId: null,
  smartEditTool: null,
  smartEditBrushSize: DEFAULT_SMART_EDIT_BRUSH_SIZE,
  smartEditBrushColor: DEFAULT_SMART_EDIT_BRUSH_COLOR,
  smartEditMaskDataUrl: null,
  smartEditPrompt: '',
  smartEditGlobalInstruction: '',
  smartEditPendingCount: 0,
  smartEditHasLocalChanges: false,
  smartEditLocalEditsDone: false,
  smartEditSubmitting: false,
  isDraggingToChat: false,
  sequenceFocusMode: false,
  activeSequenceEditorId: null,
  canvasInteractionMode: 'select',
  canvasEdges: [],
  lockedNodeIds: [],
  workflowAreaBounds: [],
  timeline: DEFAULT_TIMELINE_STATE,
};

// ==========================================
// 模板
// ==========================================

export interface BackgroundTemplate {
  id: string;
  name: string;
  category: string;
  thumbnailUrl: string;
  imageUrl: string;
}

export const TEMPLATE_CATEGORIES = [
  { id: 'all', name: 'All' },
  { id: 'office', name: 'Office' },
  { id: 'nature', name: 'Nature' },
  { id: 'tech', name: 'Tech' },
  { id: 'life', name: 'Lifestyle' },
  { id: 'solid', name: 'Solid Color' },
];

export const QUICK_PROMPTS = [
  'Minimalist tech office',
  'Cozy study room',
  'City skyline',
  'Natural scenery',
  'Gradient blue',
  'Minimalist white',
];
